import { EmptyState, PAGE_FILL, PageHeader, PageShell, PageTitle, TableCard, TransactionsIcon } from '@components'
import surfaceStyles from '@components/Layout/HeaderMenuSurface/HeaderMenuSurface.module.scss'
import settings from '@config/settings'
import { NAVIGATION_ORIGIN, TABLE_CARDS_MAX_WIDTH } from '@constants'
import cn from '@integrations/classnames'
import toast from '@integrations/notifications'
import { useContext, useEffect, useMemo, useState } from '@integrations/react'
import { useLocation, useNavigate, useParams } from '@integrations/routing'
import {
  BalanceStatementCsvModal,
  BalanceStatementPdfModal,
  CUSTOMIZE_BUTTON_MAX_WIDTH,
  RecurringPayments,
  TRANSACTIONS_TABLE_SCROLL_CLASS,
  TransactionPanel,
  TransactionsFilter,
  TransactionsTable
} from '@transactions/components'
import TransactionsTabs from '@transactions/components/TransactionsTabs'
import transactionsStyles from '@transactions/scss/transactions.module.scss'
import { isInPageSection, TRANSACTION_SECTIONS } from '@transactions/utils/sections'
import { formatDisplayDate, isOpenAccount, uiSize } from '@utils'
import {
  configuration,
  paths,
  useDownloadTransactionReportCsv,
  useMerchantAccounts,
  useTransactionsFilterQry
} from 'mmfintech-backend-api'
import type { AccountBalanceResponse, TransactionViewPortal } from 'mmfintech-backend-api/types'
import {
  GlobalContext,
  hasErrorObjectProperties,
  isValidArray,
  tr,
  useDropDownMenu,
  useMatchMedia
} from 'mmfintech-commons'
import { ErrorDisplay, Spinner } from 'mmfintech-portal-commons'

function DownloadGlyph() {
  return (
    <svg width={uiSize(13)} height={uiSize(13)} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2'>
      <path d='M12 4v11M7.5 10.5 12 15l4.5-4.5M4 19h16' />
    </svg>
  )
}

function DocGlyph() {
  return (
    <svg width={uiSize(14)} height={uiSize(14)} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
      <path d='M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z' />
      <path d='M14 3v5h5M9 13h6M9 17h6' />
    </svg>
  )
}

const TRANSACTIONS_DEFAULT_VISIBLE_FILTERS = ['amount', 'currency', 'foreignId', 'reference', 'status'] as const

function TransactionsView() {
  const { accountId: accountIdParam } = useParams<{ accountId?: string }>()
  const accountId = parseInt(accountIdParam ?? '', 10)
  const location = useLocation()
  const queryParameters = new URLSearchParams(location.search)
  const batchFileId = queryParameters.get('batchFileId')

  const { modalShow } = useContext(GlobalContext)

  const { accounts } = useMerchantAccounts()
  const openAccounts = useMemo(() => (accounts ?? []).filter(isOpenAccount), [accounts])

  const [selectedAccount, setSelectedAccount] = useState<AccountBalanceResponse>()
  const requestedSection = (location.state as { section?: string } | undefined)?.section
  const [section, setSection] = useState<string>(
    isInPageSection(requestedSection) ? requestedSection : TRANSACTION_SECTIONS.ALL
  )
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionViewPortal | null>(null)
  // Below this width the table turns into cards and the details open inside the selected card.
  const cardMode = useMatchMedia({ breakpoint: TABLE_CARDS_MAX_WIDTH, prefix: 'max' })
  const customizeAboveRows = useMatchMedia({ breakpoint: CUSTOMIZE_BUTTON_MAX_WIDTH, prefix: 'max' })

  const navigate = useNavigate()
  const exportMenu = useDropDownMenu()

  // A section asked for by another page is read once; leaving it in the history entry would reopen it after a reload.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once on mount
  useEffect(() => {
    if (requestedSection) navigate(`${location.pathname}${location.search}`, { replace: true })
  }, [])

  const filter = useTransactionsFilterQry({
    accountId,
    batchFileId: batchFileId ? parseInt(batchFileId, 10) : null,
    cookieDomain: configuration.isLocal() ? null : settings.cookieDomain,
    defaultVisible: [...TRANSACTIONS_DEFAULT_VISIBLE_FILTERS],
    isAutoReload: true
  })

  const { downloadTransactionReportCsv, downloadTransactionReportError } = useDownloadTransactionReportCsv()

  function handleDetailsClick(transactionId: number) {
    navigate(paths.banking.transactions.details(transactionId), { state: { origin: NAVIGATION_ORIGIN.TRANSACTIONS } })
  }

  function handleExportStatementPdf() {
    exportMenu.hideDropDown()
    modalShow({
      content: (
        <BalanceStatementPdfModal
          account={selectedAccount}
          accounts={accounts}
          dateFrom={filter.values.get('from')}
          dateTo={filter.values.get('to')}
        />
      ),
      options: { size: 'auto', closeOnClickOutside: false, scrollable: true }
    })
  }

  function handleExportStatementCsv() {
    exportMenu.hideDropDown()
    modalShow({
      content: (
        <BalanceStatementCsvModal
          account={selectedAccount}
          accounts={accounts}
          dateFrom={filter.values.get('from')}
          dateTo={filter.values.get('to')}
        />
      ),
      options: { size: 'auto', closeOnClickOutside: false, scrollable: true }
    })
  }

  async function handleExportClick() {
    exportMenu.hideDropDown()
    const { from, to } = filter.prepareFilter(null) || {}

    if (!from || !to) {
      toast.error(tr('FRONTEND.TRANSACTIONS.SELECT_DATE_RANGE', 'Please select a date range'))
      return
    }

    downloadTransactionReportCsv(filter.prepareFilter(null))
  }

  function handleSelectAccount(account: AccountBalanceResponse) {
    if (account == null || typeof account === 'undefined') {
      setSelectedAccount(null)
      filter.values.set('accountId', null)
      filter.values.reload()
    } else if (account.id !== selectedAccount?.id) {
      setSelectedAccount(account)
      filter.values.set('accountId', account.id)
      filter.values.reload()
    }
  }

  useEffect(() => {
    if (isValidArray(accounts) && !selectedAccount && accountId) {
      setSelectedAccount(accounts.find((account) => account.id === accountId))
    }
  }, [accounts, accountId, selectedAccount])

  useEffect(() => {
    if (
      hasErrorObjectProperties(downloadTransactionReportError) &&
      downloadTransactionReportError.errorKey &&
      ['COMMON.ERROR.VALIDATION_FAILED', 'MERCHANTS.ERROR.VALIDATION_FAILED'].includes(
        downloadTransactionReportError.errorKey
      )
    ) {
      const { cause } = (downloadTransactionReportError as any) || {}
      const { cause: errorKey } = cause[0] || {}
      toast.error(tr(errorKey))
    }
  }, [downloadTransactionReportError])

  const from = filter.values.get('from')
  const to = filter.values.get('to')
  // a new page shows other transactions: close whatever was open
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs only on page change
  useEffect(() => {
    setSelectedTransaction(null)
  }, [filter.pagination.currentPage])

  const total = filter.pagination.totalElements ?? filter.transactions?.length ?? 0
  // Where the table shows its Customize button above the rows, Export sits next to it; while the card shows a spinner, an
  // error or the empty state there is no table (and no Customize button), so Export stays in the footer.
  const tableShown = !filter.transactionsFetching && !filter.transactionsError && isValidArray(filter.transactions)
  const exportInToolbar = customizeAboveRows && tableShown

  const exportItems = [
    {
      key: 'pdf',
      tone: 'green',
      label: tr('FRONTEND.TRANSACTIONS.STATEMENT_PDF', 'Balance statement'),
      onClick: handleExportStatementPdf,
      test: 'button-export-statement-pdf'
    },
    {
      key: 'statement-csv',
      tone: 'yellow',
      label: tr('FRONTEND.TRANSACTIONS.STATEMENT_CSV', 'Statement CSV'),
      onClick: handleExportStatementCsv,
      test: 'button-export-statement-csv'
    },
    {
      key: 'csv',
      tone: 'blue',
      label: tr('FRONTEND.TRANSACTIONS.EXPORT_CSV', 'Transactions CSV'),
      onClick: handleExportClick,
      test: 'button-export-csv'
    }
  ]

  function renderExportMenu(placement: 'footer' | 'toolbar') {
    const inFooter = placement === 'footer'

    return (
      <div
        className={cn(surfaceStyles['header-menu-wrapper'], 'export-menu', {
          [transactionsStyles['export-menu--footer']]: inFooter
        })}
        ref={exportMenu.setRef}
      >
        <button
          type='button'
          className={cn(
            surfaceStyles['header-menu-trigger'],
            surfaceStyles['header-menu-pill'],
            'header-menu-pill',
            'export-trigger',
            { [surfaceStyles['opened']]: exportMenu.visible }
          )}
          aria-haspopup='menu'
          aria-expanded={exportMenu.visible}
          onClick={exportMenu.toggleDropDown}
          data-test='button-export'
        >
          <DownloadGlyph />
          {tr('FRONTEND.TRANSACTIONS.EXPORT', 'Export')}
        </button>
        {exportMenu.visible && (
          <div
            role='menu'
            className={cn(
              surfaceStyles['header-menu-dropdown'],
              surfaceStyles['opened'],
              { [surfaceStyles['upward']]: inFooter },
              transactionsStyles['export-dropdown']
            )}
          >
            {exportItems.map((item) => (
              <button
                key={item.key}
                type='button'
                role='menuitem'
                className={cn(surfaceStyles['header-menu-option'], 'dropdown-option')}
                onClick={item.onClick}
                data-test={item.test}
              >
                <span
                  className={cn(
                    transactionsStyles['export-icon'],
                    transactionsStyles[`export-icon--${item.tone}`],
                    'export-icon'
                  )}
                >
                  <DocGlyph />
                </span>
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <PageShell fill className={cn('transactions-wrapper')}>
      <PageHeader>
        <PageTitle>{tr('FRONTEND.TRANSACTIONS.TITLE', 'Transactions')}</PageTitle>
      </PageHeader>

      <div className={cn(transactionsStyles['transactions-page'], 'transactions-page', PAGE_FILL.item)}>
        <TransactionsTabs active={section} onSectionChange={setSection} />

        {section === TRANSACTION_SECTIONS.RECURRING ? (
          <RecurringPayments />
        ) : (
          <>
            <TransactionsFilter
              filter={filter}
              accounts={openAccounts}
              selectedAccount={selectedAccount}
              onSelectAccount={handleSelectAccount}
            />
            <ErrorDisplay error={downloadTransactionReportError} />

            <div className={cn(transactionsStyles['transactions-content'], 'transactions-content', PAGE_FILL.item)}>
              <TableCard
                className={cn(
                  transactionsStyles['transactions-pagination-container'],
                  'transactions-pagination-container'
                )}
                fallback={
                  filter.transactionsFetching ? (
                    <Spinner />
                  ) : filter.transactionsError ? (
                    <ErrorDisplay error={filter.transactionsError} />
                  ) : (
                    !isValidArray(filter.transactions) && (
                      <EmptyState
                        icon={<TransactionsIcon size={48} />}
                        title={tr(
                          'FRONTEND.BANKING.TRANSACTIONS.NO_TRANSACTIONS',
                          'No transactions for the selected period or search criteria'
                        )}
                        data-test='transactions-empty'
                      />
                    )
                  )
                }
                pagination={filter.pagination.register()}
                scrollProps={{ className: TRANSACTIONS_TABLE_SCROLL_CLASS }}
                footer={
                  <div
                    className={cn(transactionsStyles['transactions-footer-container'], 'transactions-footer-container')}
                  >
                    <span className={cn(transactionsStyles['transactions-summary'], 'transactions-summary')}>
                      {tr('FRONTEND.TRANSACTIONS.SUMMARY', '{{COUNT}} transactions', { COUNT: total })}
                      {from && to ? ` · ${formatDisplayDate(from)} – ${formatDisplayDate(to)}` : ''}
                    </span>
                    {!exportInToolbar && renderExportMenu('footer')}
                  </div>
                }
              >
                <TransactionsTable
                  transactions={filter.transactions}
                  reload={() => filter.pagination.reload()}
                  selectedId={selectedTransaction?.id}
                  onSelect={(transaction) =>
                    setSelectedTransaction(cardMode && transaction.id === selectedTransaction?.id ? null : transaction)
                  }
                  cardMode={cardMode}
                  toolbarActions={exportInToolbar ? renderExportMenu('toolbar') : null}
                  renderDetails={(transaction) => (
                    <TransactionPanel
                      embedded
                      transaction={transaction}
                      onClose={() => setSelectedTransaction(null)}
                      onOpenDetails={handleDetailsClick}
                    />
                  )}
                />
              </TableCard>

              {selectedTransaction && !cardMode && (
                <TransactionPanel
                  transaction={selectedTransaction}
                  onClose={() => setSelectedTransaction(null)}
                  onOpenDetails={handleDetailsClick}
                />
              )}
            </div>
          </>
        )}
      </div>
    </PageShell>
  )
}

export default TransactionsView
